import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { brlCompact, num } from "../components/ui.js";

interface ResumoDaBase {
  viagens: number | null;
  freteTotal: number | null;
  complianceVencida: number | null;
  inconsistencias: number | null;
}

const focus = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-4";

function Icone({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={`h-6 w-6 shrink-0 ${className}`}>
      {children}
    </svg>
  );
}

function Acesso({ to, title, description, icon }: { to: string; title: string; description: string; icon: ReactNode }) {
  return (
    <Link to={to} className={`group flex min-h-24 items-center gap-4 rounded-lg px-3 py-4 hover:bg-brand-50 ${focus}`}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 group-hover:bg-brand-100">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900 group-hover:text-brand-700">{title}</span>
        <span className="mt-1 block text-sm leading-5 text-slate-500">{description}</span>
      </span>
      <Icone className="!h-4 !w-4 text-brand-600"><path d="m9 5 7 7-7 7" /></Icone>
    </Link>
  );
}

function NumeroDaBase({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs leading-5 text-slate-600">{label}</dt>
      <dd className="mt-1 break-words text-xl font-semibold tracking-tight text-brand-900 tabular-nums">{value}</dd>
    </div>
  );
}

export function Inicio() {
  const { user, canArea, canWrite } = useAuth();
  const primeiroNome = user?.name.trim().split(/\s+/)[0];
  const dataHoje = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long", day: "numeric", month: "long", timeZone: "America/Sao_Paulo",
  }).format(new Date());
  const resumo = useQuery({
    queryKey: ["kpis"],
    queryFn: async () => (await api.get<ResumoDaBase>("/insights/dashboard")).data,
    enabled: canArea("dashboard"),
  });
  const valor = (n: number | null | undefined, moeda = false) =>
    n == null || !Number.isFinite(n) ? "Indisponível" : moeda ? brlCompact(n) : num(n);

  return (
    <div className="mx-auto max-w-6xl pb-6">
      <div className="mb-5 flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="font-medium text-slate-700">Olá{primeiroNome ? `, ${primeiroNome}` : ""}.</p>
        <p className="text-slate-500">{dataHoje}</p>
      </div>

      <section aria-labelledby="inicio-title" className="overflow-hidden rounded-2xl border border-brand-100 bg-white">
        <div className="grid items-center gap-2 px-5 py-7 sm:grid-cols-[1fr_180px] sm:gap-8 sm:px-8 sm:py-8 lg:grid-cols-[1fr_220px]">
          <div>
            <h1 id="inicio-title" className="max-w-xl text-3xl font-semibold leading-tight tracking-tight text-brand-900 lg:text-4xl">Sua operação começa aqui</h1>
            <p className="mt-3 max-w-lg text-sm leading-6 text-slate-600 sm:text-base">Acompanhe as cargas, organize a programação e encontre as informações da frota em um só lugar.</p>
            {canArea("paineis") && (
              <Link to="/torre" className={`mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-3 text-sm font-semibold text-white hover:bg-brand-700 ${focus}`}>
                <Icone className="!h-5 !w-5"><path d="M12 3v3m0 12v3M3 12h3m12 0h3" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></Icone>
                Abrir Torre de Controle
              </Link>
            )}
          </div>
          <img src="/icons/caminhao-brasil-novo.png" alt="" width="220" height="220" className="hidden aspect-square w-full object-contain sm:block" />
        </div>

        <div className="h-1 w-20 bg-gold-400" />

        <div className="grid border-t border-slate-100 lg:grid-cols-[1.15fr_1fr]">
          <section aria-labelledby="acessos-title" className="px-3 py-6 sm:px-6">
            <h2 id="acessos-title" className="mb-2 px-3 text-base font-semibold text-slate-900">O que você precisa fazer?</h2>
            <div className="divide-y divide-slate-100">
              {canArea("vinculo") && <Acesso to="/vincular" title="Vincular carga" description="Escolha motorista e placas por disponibilidade e distância da coleta." icon={<Icone><path d="M4 8h16m-4-4 4 4-4 4M20 16H4m4-4-4 4 4 4" /></Icone>} />}
              {canArea("programacao") && (
                <Acesso to="/programacao" title={canWrite("data_programacao") ? "Planejar carregamentos" : "Consultar programação"} description="Veja a programação de cargas e os agendamentos." icon={<Icone><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M8 3v4m8-4v4M4 11h16M8 15h3m-3 3h6" /></Icone>} />
              )}
              {canArea("cadastros") && (
                <Acesso to="/cadastros" title="Consultar veículos e motoristas" description="Encontre placas, contatos e informações da frota." icon={<Icone><path d="M3 6h11v11H3zM14 10h4l3 4v3h-7" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></Icone>} />
              )}
              {canArea("dashboard") && (
                <Acesso to="/indicadores" title="Acompanhar resultados" description="Consulte viagens, fretes e a evolução da operação." icon={<Icone><path d="M4 3v17h17M8 16v-5m5 5V7m5 9V4" /></Icone>} />
              )}
            </div>
          </section>

          {canArea("dashboard") && (
            <section aria-labelledby="resumo-title" className="border-t border-slate-100 bg-brand-50/50 px-6 py-6 sm:px-8 lg:border-l lg:border-t-0">
              <h2 id="resumo-title" className="text-base font-semibold text-slate-900">Resumo da base</h2>
              <p className="mt-1 max-w-sm text-xs leading-5 text-slate-500">Totais acumulados dos registros disponíveis, sem filtro de período.</p>
              {resumo.isPending ? (
                <p role="status" className="py-8 text-sm text-slate-600">Carregando o resumo da base…</p>
              ) : resumo.isError ? (
                <div role="alert" className="mt-5">
                  <p className="text-sm leading-6 text-slate-600">Não foi possível carregar os indicadores. Os acessos da página continuam disponíveis.</p>
                  <button type="button" disabled={resumo.isFetching} onClick={() => void resumo.refetch()} className={`mt-3 min-h-11 rounded-lg border border-brand-200 bg-white px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60 ${focus}`}>
                    {resumo.isFetching ? "Carregando…" : "Tentar novamente"}
                  </button>
                </div>
              ) : (
                <dl className="mt-6 grid grid-cols-2 gap-x-5 gap-y-6">
                  <NumeroDaBase label="Viagens registradas" value={valor(resumo.data?.viagens)} />
                  <NumeroDaBase label="Frete acumulado" value={valor(resumo.data?.freteTotal, true)} />
                  <NumeroDaBase label="Cursos e exames vencidos na base" value={valor(resumo.data?.complianceVencida)} />
                  <NumeroDaBase label="Inconsistências catalogadas" value={valor(resumo.data?.inconsistencias)} />
                </dl>
              )}
            </section>
          )}
        </div>
      </section>

      {(canArea("operacoes") || canArea("dados") || canArea("inconsistencias")) && (
      <section aria-labelledby="conferencia-title" className="mt-8 px-1">
        <div className="flex items-start gap-3">
          <Icone className="mt-0.5 text-slate-500"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 10v10m6-10v10" /></Icone>
          <div>
            <h2 id="conferencia-title" className="text-base font-semibold text-slate-800">Tabelas e conferência</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">As tabelas completas continuam disponíveis para consultar registros, conferir informações e investigar divergências.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 sm:pl-9">
          {canArea("operacoes") && <Link to="/operacoes" className={`inline-flex min-h-11 items-center rounded-lg border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 ${focus}`}>Tabelas de operações</Link>}
          {canArea("dados") && <Link to="/dados" className={`inline-flex min-h-11 items-center rounded-lg border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 ${focus}`}>Central de dados</Link>}
          {canArea("inconsistencias") && <Link to="/inconsistencias" className={`inline-flex min-h-11 items-center rounded-lg border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 ${focus}`}>Ver inconsistências</Link>}
        </div>
      </section>
      )}
    </div>
  );
}
